// discoveryRule_renderer.js
// Renders the discoveryRule dashboard from JSON insights into a DOM container

export function renderDiscoveryRuleDashboardFromJSON(data, container) {
  if (!data || !container) {
    console.error('Invalid data or container');
    return;
  }

  // ---------- Helpers ----------
  const fmtSeconds = (sec) => {
    if (typeof sec !== 'number' || !isFinite(sec)) return '—';
    const h = Math.floor(sec / 3600);
    const d = Math.floor(sec / 86400);
    return `${sec.toLocaleString()} s (≈ ${h} h / ${d} days)`;
  };

  const pickFirstSentence = (txt = '') => {
    const s = (txt || '').split('.').filter(Boolean).map(t => t.trim());
    return s.length ? `${s[0]}.` : (txt || '');
  };

  const pickImpactSentence = (txt = '', kpis = {}) => {
    const sents = (txt || '').split('.').filter(Boolean).map(t => t.trim());
    const hit = sents.find((s, i) =>
      /impact|performance|significant/i.test(s) || (i > 0 && /average/i.test(s))
    );
    if (hit) return `${hit}.`;
    // Fallback
    const pct = kpis?.affected_pct != null ? `${kpis.affected_pct}%` : '—';
    const dur = fmtSeconds(kpis?.avg_duration_sec ?? NaN);
    return `${pct} of incidents with an average resolution duration of approximately ${dur}`;
  };

  // ---------- Summary parsing ----------
  const discoveryRuleText = pickFirstSentence(data.summary || '');
  const impactText = pickImpactSentence(data.summary || '', data.kpis || {});

  // ---------- HTML ----------
  let html = '';

  // Summary
  html += `
    <div class="section">
      <div class="section-header">
        <div class="section-icon" aria-hidden="true">🧩</div>
        <h2 class="section-title">Summary</h2>
      </div>
      <div class="section-body">
        <p><strong>DiscoveryRule:</strong> ${discoveryRuleText}</p>
        <p><strong>Issue Type:</strong> ${data?.kpis?.issue_type ?? '—'}</p>
        <p><strong>Impact:</strong> ${impactText}</p>

        <div class="kpi-block">
          <div class="kpi-title">Average duration</div>
          <ul class="kpi-list indent-list">
            <li>Mean ≈ ${fmtSeconds(data?.kpis?.avg_duration_sec)}</li>
            ${data?.kpis?.median_duration_sec ? `<li>Median ≈ ${fmtSeconds(data.kpis.median_duration_sec)}</li>` : ''}
          </ul>
        </div>
      </div>
    </div>
  `;

  // Observed Patterns
  if (data?.observed_patterns?.attributes?.length) {
    html += `
      <div class="section">
        <div class="section-header">
          <div class="section-icon" aria-hidden="true">📊</div>
          <h2 class="section-title">Observed Data Patterns</h2>
        </div>
        <div class="section-body">
          <div class="table-container">
            <table class="data-table">
              <thead>
                <tr>
                  <th>Attribute</th>
                  <th>Observed Pattern</th>
                  <th>Possible Interpretation</th>
                </tr>
              </thead>
              <tbody>
                ${data.observed_patterns.attributes.map(a => `
                  <tr>
                    <td><span class="pill">${escapeHtml(a.attribute || '—')}</span></td>
                    <td>${escapeHtml(a.observed_pattern || '—')}</td>
                    <td class="muted">${escapeHtml(a.possible_interpretation || '—')}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    `;
  }

  // Likely Primary Causes
  if (Array.isArray(data?.likely_primary_causes) && data.likely_primary_causes.length) {
    html += `
      <div class="section">
        <div class="section-header">
          <div class="section-icon" aria-hidden="true">🔍</div>
          <h2 class="section-title">Likely Primary Causes</h2>
        </div>
        <div class="section-body">
          <ul class="arrow-list">
            ${data.likely_primary_causes.map((c, i) => `<li><strong>${i + 1}.</strong>&nbsp;${escapeHtml(c)}</li>`).join('')}
          </ul>
        </div>
      </div>
    `;
  }

  // Root-Cause Hypotheses
  if (Array.isArray(data?.root_cause_hypotheses) && data.root_cause_hypotheses.length) {
    html += `
      <div class="section">
        <div class="section-header">
          <div class="section-icon" aria-hidden="true">🧠</div>
          <h2 class="section-title">Root-Cause Hypotheses</h2>
        </div>
        <div class="section-body">
          <div class="table-container">
            <table class="data-table">
              <thead>
                <tr>
                  <th>Hypothesis</th>
                  <th>Supporting Evidence</th>
                </tr>
              </thead>
              <tbody>
                ${data.root_cause_hypotheses.map(h => `
                  <tr>
                    <td><strong>${escapeHtml(h.hypothesis || '—')}</strong></td>
                    <td class="muted">${escapeHtml(h.supporting_evidence || '—')}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    `;
  }

  // Optimization Plan
  if (data?.optimization_plan) {
    html += `
      <div class="section">
        <div class="section-header">
          <div class="section-icon" aria-hidden="true">🧰</div>
          <h2 class="section-title">Optimization Plan</h2>
        </div>
        <div class="section-body">
          ${Array.isArray(data.optimization_plan.process_changes) && data.optimization_plan.process_changes.length ? `
            <div class="subsection">
              <h3>A. Process Changes</h3>
              <ul class="arrow-list">
                ${data.optimization_plan.process_changes.map(t => `<li>${escapeHtml(t)}</li>`).join('')}
              </ul>
            </div>
          ` : ''}

          ${Array.isArray(data.optimization_plan.jira_configuration) && data.optimization_plan.jira_configuration.length ? `
            <div class="subsection">
              <h3>B. Jira Configuration</h3>
              <ul class="arrow-list">
                ${data.optimization_plan.jira_configuration.map(t => `<li>${escapeHtml(t)}</li>`).join('')}
              </ul>
            </div>
          ` : ''}

          ${Array.isArray(data.optimization_plan.automation_rules) && data.optimization_plan.automation_rules.length ? `
            <div class="subsection">
              <h3>C. Automation Rules</h3>
              <div class="card-grid">
                ${data.optimization_plan.automation_rules.map(r => `
                  <div class="card">
                    <div class="card-title">${escapeHtml(r.platform || 'Rule')}</div>
                    <div class="card-body muted">${escapeHtml(r.rule || '')}</div>
                  </div>
                `).join('')}
              </div>
            </div>
          ` : ''}

          ${Array.isArray(data.optimization_plan.behavioural_nudges) && data.optimization_plan.behavioural_nudges.length ? `
            <div class="subsection">
              <h3>D. Behavioral Nudges</h3>
              <ul class="arrow-list">
                ${data.optimization_plan.behavioural_nudges.map(t => `<li>${escapeHtml(t)}</li>`).join('')}
              </ul>
            </div>
          ` : ''}
        </div>
      </div>
    `;
  }

  // Risks & Mitigations
  if (Array.isArray(data?.risks_and_mitigations) && data.risks_and_mitigations.length) {
    html += `
      <div class="section">
        <div class="section-header">
          <div class="section-icon" aria-hidden="true">🚨</div>
          <h2 class="section-title">Risks & Mitigations</h2>
        </div>
        <div class="section-body">
          <div class="risk-grid">
            ${data.risks_and_mitigations.map(rm => `
              <div class="risk-item">
                <div>
                  <div class="risk-label">Risk</div>
                  <div>${escapeHtml(rm.risk || '—')}</div>
                </div>
                <div>
                  <div class="mitigation-label">Mitigation</div>
                  <div class="muted">${escapeHtml(rm.mitigation || '—')}</div>
                </div>
              </div>
            `).join('')}
          </div>
        </div>
      </div>
    `;
  }

  // Key Signals
  if (Array.isArray(data?.key_signals) && data.key_signals.length) {
    html += `
      <div class="section">
        <div class="section-header">
          <div class="section-icon" aria-hidden="true">📈</div>
          <h2 class="section-title">Key Signals for Ongoing Monitoring</h2>
        </div>
        <div class="section-body">
          <ul class="metric-list">
            ${data.key_signals.map(s => `<li>${escapeHtml(s)}</li>`).join('')}
          </ul>
        </div>
      </div>
    `;
  }

  // Executive Summary (optional)
  const executiveSummary = generateExecutiveSummary(data);
  if (executiveSummary) {
    html += `
      <div class="executive-summary">
        <h2>✅ Executive Summary</h2>
        ${executiveSummary}
      </div>
    `;
  }

  // ---------- Styles (no inline layout styles) ----------
  const styles = `
    <style>
      :root{
        --ink:#172b4d;
        --muted:#6b778c;
        --surface:#fff;
        --line:#dfe1e6;
        --line-soft:#ebecf0;
        --brand:#0052cc;
        --brand-2:#0747a6;
        --accent:#00875a;
        --danger:#de350b;
        --bg:#f4f5f7;
        --radius:6px;
        --space-4:4px; --space-8:8px; --space-12:12px; --space-16:16px; --space-20:20px; --space-24:24px;
      }

.section {
  margin: var(--space-16) 0 var(--space-24);
}

/* Tighter first section */
.section:first-of-type {
  margin-top: var(--space-4);
}

.section-header {
  display: flex;
  align-items: center; /* perfect vertical alignment */
  gap: var(--space-8);
  padding-bottom: var(--space-8);
  border-bottom: 2px solid var(--brand);
}

/* Bigger, properly aligned icon */
.section-icon {
  inline-size: 36px;
  block-size: 36px;
  display: grid;
  place-items: center;
  font-size: 20px; /* emoji/icon size */
  border-radius: 8px;
  background: var(--bg);
  color: var(--brand-2);
  flex: 0 0 36px;
  margin-top: 0; /* remove old baseline nudge */
}

.section-title {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: var(--ink);
  line-height: 1.2;
}

      .section-body{
        padding: var(--space-12) 0 0 var(--space-4);
        display: grid;
        gap: var(--space-12);
      }
      .section-body p{ margin:0; line-height:1.6; font-size:15px; }

      .kpi-block{ margin-top: var(--space-8); }
      .kpi-title{ font-weight:600; color:var(--ink); margin-bottom: var(--space-8); }
      .kpi-list{ margin:0; }
      .indent-list{ padding-left: 1.5rem; }

      .arrow-list{ list-style:none; margin:0; padding:0; }
      .arrow-list li{
        display:flex; gap: var(--space-8); align-items:flex-start;
        padding: var(--space-8) 0;
        border-bottom:1px solid var(--line-soft);
      }
      .arrow-list li::before{
        content:"→"; color:var(--brand); font-weight:700; margin-top:1px;
      }
      .arrow-list li:last-child{ border-bottom:none; }

      .table-container{ overflow:auto; border:1px solid var(--line); border-radius: var(--radius); }
      .data-table{ width:100%; border-collapse: collapse; background:var(--surface); }
      .data-table thead th{
        position:sticky; top:0; z-index:1;
        background:var(--bg); color:#42526e; text-transform:uppercase;
        font-size:12px; letter-spacing:.02em;
        padding:8px 12px; border-bottom:2px solid var(--line);
      }
      .data-table tbody td{
        padding:10px 12px; border-bottom:1px solid var(--line-soft); font-size:14px;
      }
      .data-table tbody tr:nth-child(even){ background:#fafbfc; }
      .data-table tbody tr:hover{ background:var(--bg); }

      .pill{
        display:inline-block; padding:2px 8px; border-radius:999px;
        background:#e9f2ff; color:#0c66e4; font-size:12px; line-height:18px;
        border:1px solid #cce0ff;
      }

      .card-grid{ display:grid; grid-template-columns: repeat(auto-fill, minmax(260px,1fr)); gap: var(--space-12); }
      .card{
        background:var(--surface); border:1px solid var(--line);
        border-radius: var(--radius); padding: var(--space-12) var(--space-12);
      }
      .card-title{ color:#0c66e4; font-weight:600; margin-bottom: var(--space-8); }
      .card-body{ font-size:14px; }

      .risk-grid{ display:grid; gap: var(--space-12); }
      .risk-item{
        display:grid; grid-template-columns:1fr 1fr; gap: var(--space-16);
        background:var(--surface); border:1px solid var(--line); border-radius: var(--radius); padding: var(--space-12);
      }
      .risk-label{ font-weight:700; color:var(--danger); margin-bottom: var(--space-4); }
      .mitigation-label{ font-weight:700; color:var(--accent); margin-bottom: var(--space-4); }

      .metric-list{ list-style:none; padding:0; margin:0; display:grid; gap: var(--space-8); }
      .metric-list li{
        background:var(--surface); border:1px solid var(--line); border-left:3px solid var(--accent);
        padding: var(--space-8) var(--space-12); border-radius: var(--radius); font-size:14px;
      }

      .subsection{ margin-top: var(--space-12); }
      .subsection h3{ margin:0 0 var(--space-8); color:#0c66e4; font-size:16px; font-weight:600; }

      .muted{ color:var(--muted); }

      .executive-summary{
        background: linear-gradient(135deg, var(--accent) 0%, #00a876 100%);
        color:#fff; border-radius: var(--radius);
        padding: 0 var(--space-24) var(--space-20);
        margin-top: var(--space-24);
      }

      .executive-summary h2{ margin:0 0 var(--space-8); font-size:20px; }
      .executive-summary p{ margin:0 0 var(--space-8); font-size:15px; line-height:1.6; }
    </style>
  `;

  container.innerHTML = styles + html;
}

// Small HTML escaper for table/list content
function escapeHtml(s){
  if (s == null) return '';
  return String(s)
    .replace(/&/g,'&amp;')
    .replace(/</g,'&lt;')
    .replace(/>/g,'&gt;')
    .replace(/"/g,'&quot;')
    .replace(/'/g,'&#39;');
}

// Helper function to generate executive summary based on data
function generateExecutiveSummary(data) {
  if (!data?.kpis) return null;

  const affectedPct = data.kpis.affected_pct;
  const primaryCause = data.kpis.likely_primary_cause || data.likely_primary_causes?.[0];

  const suggestedActions = [];
  if (data.optimization_plan?.process_changes?.length) {
    suggestedActions.push(...data.optimization_plan.process_changes.slice(0, 2));
  }
  if (data.optimization_plan?.automation_rules?.length) {
    suggestedActions.push('implement automation rules');
  }
  if (!primaryCause && suggestedActions.length === 0) return null;

  let summary = `<p>`;
  if (affectedPct != null) {
    summary += `A significant share of incidents (${affectedPct}%) are affected by this issue`;
    if (primaryCause) summary += `, primarily due to <strong>${escapeHtml(primaryCause)}</strong>`;
    summary += `.`;
  } else if (primaryCause) {
    summary += `This issue is primarily caused by <strong>${escapeHtml(primaryCause)}</strong>.`;
  }
  summary += `</p>`;

  if (suggestedActions.length) {
    summary += `<p>To optimize, `;
    summary += suggestedActions
      .map(rec => `<strong>${escapeHtml(String(rec).toLowerCase().replace(/^(introduce|implement|add|re-evaluate)\s+/i, ''))}</strong>`)
      .join(', ');
    summary += `. These changes can significantly improve incident management performance.</p>`;
  }

  return summary;
}
